import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { API_ORIGIN } from '@/lib/config'
import type { BadgeProps } from '@/components/ui/badge'
import { currentLocale, i18n } from '@/lib/i18n'

// ── Envelope backend { success, data, meta } ─────────────────────────
interface Envelope<T> {
  success: boolean
  data: T
  meta?: { page: number; pageSize: number; total: number; totalPages: number }
}

// ── Nhãn tiếng Việt & hằng số ────────────────────────────────────────
export const PRICE_TYPES = ['fixed', 'range', 'contact', 'free', 'description'] as const
export type PriceType = (typeof PRICE_TYPES)[number]

export const PRICE_TYPE_LABELS: Record<string, string> = {
  get fixed() { return i18n.t('aiComp:products.priceType.fixed') },
  get range() { return i18n.t('aiComp:products.priceType.range') },
  get contact() { return i18n.t('aiComp:products.priceType.contact') },
  get free() { return i18n.t('aiComp:products.priceType.free') },
  get description() { return i18n.t('aiComp:products.priceType.description') },
}

export const PRODUCT_STATUSES = ['active', 'draft', 'archived'] as const
export type ProductStatus = (typeof PRODUCT_STATUSES)[number]

export const PRODUCT_STATUS_LABELS: Record<string, string> = {
  get active() { return i18n.t('aiComp:products.status.active') },
  get draft() { return i18n.t('aiComp:products.status.draft') },
  get archived() { return i18n.t('aiComp:products.status.archived') },
}

export function productStatusVariant(status?: string | null): BadgeProps['variant'] {
  switch (status) {
    case 'active':
      return 'success'
    case 'draft':
      return 'secondary'
    case 'archived':
      return 'outline'
    default:
      return 'secondary'
  }
}

/** Định dạng giá theo priceType để hiển thị trong bảng/chi tiết. */
export function formatProductPrice(p: Pick<Product, 'priceType' | 'price' | 'priceMax' | 'currency'>): string {
  const cur = p.currency || 'VND'
  const money = (v: number | null | undefined) =>
    v == null ? '—' : `${new Intl.NumberFormat(currentLocale()).format(v)} ${cur}`
  switch (p.priceType) {
    case 'free':
      return i18n.t('aiComp:products.priceType.free')
    case 'contact':
      return i18n.t('aiComp:products.priceType.contact')
    case 'description':
      return i18n.t('aiComp:products.priceType.description')
    case 'range':
      if (p.price != null && p.priceMax != null) return `${money(p.price)} – ${money(p.priceMax)}`
      return money(p.price ?? p.priceMax)
    case 'fixed':
    default:
      return money(p.price)
  }
}

/** Ghép API_ORIGIN nếu URL ảnh là đường dẫn tương đối. */
export function resolveImageUrl(url?: string | null): string | undefined {
  if (!url) return undefined
  if (/^https?:\/\//i.test(url)) return url
  return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`
}

// ── Kiểu dữ liệu ─────────────────────────────────────────────────────
export interface ProductCategoryLite {
  id: string
  name: string
}

export interface Product {
  id: string
  categoryId: string | null
  name: string
  code: string | null
  slug: string
  description: string | null
  notes: string | null
  keywords: string | null
  tags: string[]
  priceType: string
  price: number | null
  priceMax: number | null
  currency: string
  specs: Record<string, unknown>
  images: string[]
  status: string
  sortOrder: number
  source: string
  /** Có giá trị = bản sao chỉ đọc đồng bộ từ hệ thống ngoài (vd 'evotech'). */
  externalSource?: string | null
  externalId?: string | null
  externalParentId?: string | null
  /** itemsCode — mã sản phẩm trên Evotech (biến {itemsCode} của mẫu link). */
  externalCode?: string | null
  stockAvailable?: number | null
  externalUrl?: string | null
  createdAt: string
  updatedAt: string
  category: ProductCategoryLite | null
}

export const PRODUCT_SOURCE_LABELS: Record<string, string> = {
  evotech: 'Evotech',
}

/** Sản phẩm đồng bộ từ hệ thống ngoài → chỉ đọc trên eCDP. */
export function isSyncedProduct(p: Pick<Product, 'externalSource'>): boolean {
  return !!p.externalSource
}

export interface ProductCategory {
  id: string
  parentId: string | null
  name: string
  slug: string
  description: string | null
  icon: string | null
  sortOrder: number
  productCount: number
  createdAt: string
  updatedAt: string
}

export interface ProductInput {
  name: string
  code?: string | null
  categoryId?: string | null
  description?: string | null
  notes?: string | null
  keywords?: string | null
  tags?: string[]
  priceType?: string
  price?: number | null
  priceMax?: number | null
  currency?: string
  images?: string[]
  status?: string
}

export interface ProductCategoryInput {
  name: string
  parentId?: string | null
  description?: string | null
  icon?: string | null
  sortOrder?: number
}

// ── Products query/mutation ──────────────────────────────────────────
export interface ProductQueryParams {
  page?: number
  pageSize?: number
  search?: string
  categoryId?: string
  status?: string
  /** manual | import | evotech … hoặc 'external' / 'internal' */
  source?: string
  /** Đúng một nguồn đồng bộ (evotech…) hoặc 'internal' = tạo trong eCDP */
  externalSource?: string
  /** 'group' = biến thể cùng nhóm đứng liền nhau */
  sort?: string
}

export interface ProductListResult {
  items: Product[]
  meta: { page: number; pageSize: number; total: number; totalPages: number }
}

export function useProducts(params: ProductQueryParams) {
  return useQuery<ProductListResult>({
    queryKey: ['products', params],
    queryFn: async () => {
      const { data } = await api.get<Envelope<Product[]>>('/products', { params })
      return {
        items: data.data,
        meta: data.meta ?? { page: 1, pageSize: 20, total: data.data.length, totalPages: 1 },
      }
    },
    placeholderData: (prev) => prev,
  })
}

export function useCreateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ProductInput) => {
      const { data } = await api.post<Envelope<Product>>('/products', input)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  })
}

export function useUpdateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; data: Partial<ProductInput> }) => {
      const { data } = await api.patch<Envelope<Product>>(`/products/${vars.id}`, vars.data)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  })
}

export function useDeleteProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<Envelope<{ deleted: boolean }>>(`/products/${id}`)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['products'] }),
  })
}

/** Upload ảnh sản phẩm → trả URL (tương đối). */
export function useUploadProductImage() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const { data } = await api.post<Envelope<{ url: string }>>('/products/upload-image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return data.data.url
    },
  })
}

// ── Product categories query/mutation ────────────────────────────────
export function useProductCategories() {
  return useQuery<ProductCategory[]>({
    queryKey: ['product-categories'],
    queryFn: async () => {
      const { data } = await api.get<Envelope<ProductCategory[]>>('/product-categories')
      return data.data
    },
  })
}

export function useCreateProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ProductCategoryInput) => {
      const { data } = await api.post<Envelope<ProductCategory>>('/product-categories', input)
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-categories'] }),
  })
}

export function useUpdateProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (vars: { id: string; data: Partial<ProductCategoryInput> }) => {
      const { data } = await api.patch<Envelope<ProductCategory>>(
        `/product-categories/${vars.id}`,
        vars.data,
      )
      return data.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['product-categories'] }),
  })
}

export function useDeleteProductCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<Envelope<{ deleted: boolean }>>(`/product-categories/${id}`)
      return data.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-categories'] })
      qc.invalidateQueries({ queryKey: ['products'] })
    },
  })
}

// ── Bộ chọn "Sản phẩm" ở khung chat ──────────────────────────────────

export interface ProductPickerSources {
  sources: { source: string; count: number }[]
  evotech: { connected: boolean; webappUrlTemplate: string | null; miniAppUrlTemplate: string | null }
}

export function useProductPickerSources(enabled: boolean) {
  return useQuery<ProductPickerSources>({
    queryKey: ['products', 'picker-sources'],
    queryFn: async () => (await api.get<Envelope<ProductPickerSources>>('/products/picker-sources')).data.data,
    enabled,
    staleTime: 60_000,
  })
}

const PICKER_PAGE_SIZE = 30

/** Danh sách sản phẩm đang bán của một nguồn, tải dần ("Xem thêm"). */
export function useProductPickerList(params: { externalSource?: string; search?: string }, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['products', 'picker', params],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<Envelope<Product[]>>('/products', {
        params: {
          ...params,
          status: 'active',
          sort: 'group',
          page: pageParam,
          pageSize: PICKER_PAGE_SIZE,
        },
      })
      return { items: data.data, meta: data.meta ?? { page: pageParam, pageSize: PICKER_PAGE_SIZE, total: data.data.length, totalPages: 1 } }
    },
    getNextPageParam: (last) => (last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined),
    enabled,
    staleTime: 30_000,
  })
}

export interface SendProductResult {
  link: string | null
  imageSent: boolean
  imageError: string | null
}

/** Gửi sản phẩm cho khách — link dựng ở máy chủ theo mẫu của tổ chức. */
export function useSendProduct(convId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: { productId: string; linkType?: 'webapp' | 'miniapp' }) =>
      (await api.post<SendProductResult>(`/conversations/${convId}/send-product`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conversation-messages', convId] })
      qc.invalidateQueries({ queryKey: ['conversations'] })
    },
  })
}
